/* CasesPipelineCompliance — Phase 2 of the Aligner Cases redesign (iter98.24).
 *
 * Replaces the AlignerInsights bridge that the Pipeline shipped with in
 * iter98.23. Translates the HTML mockup into our Apollo cyan-teal palette
 * with NO emerald carryover. All thresholds and bucket math reuse the
 * existing /doctor/insights/compliance endpoint so the donuts, sparklines
 * and threshold editor stay perfectly in sync.
 *
 * Sections:
 *   1. Compliance KPI strip — at-risk + critical headline counts
 *   2. 4 donut cards — Aligner pace · Wear discipline · Check-in streak · Reported concerns
 *   3. My-patients wear-trend line chart — 30 days, target marker
 *   4. Per-patient table — sparkline + bucket pill + last-touch + actions
 *   5. Alert threshold editor — wear / log-staleness sliders persisted to backend
 *
 * Data sources (all existing — single batch fetched on mount):
 *   • /doctor/insights/compliance     — per-case items + buckets + thresholds
 *   • /doctor/insights/aligner        — wear / check-in / issues aggregates
 *   • /doctor/insights/wear-trend     — 30-day avg across doctor's in-treatment cases
 *   • /doctor/compliance-thresholds   — editor seed
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { waLink, waReason } from "@/lib/wa";
import { Button } from "@/components/ui/button";
import {
    MagnifyingGlass, ChartLine, TrendUp, TrendDown, ChatCircleDots,
    CalendarBlank, UserCircle, Sliders, FloppyDisk, ArrowsClockwise,
    Warning, CheckCircle, Info, Bell, EnvelopeSimple, PaperPlaneTilt,
    // iter98.99dh — Compliance & Monitoring 2-state visual system.
    ShieldCheck, Stethoscope,
} from "@phosphor-icons/react";
import Sparkline from "@/components/doctor/Sparkline";
import PatientInsightsDrawer from "@/components/doctor/PatientInsightsDrawer";
import { toast } from "sonner";


/* ────────────────────────────────────────────────────────────────────
   PALETTE — Apollo cyan-teal, sourced from index.css CSS variables.
   Hex literals are only used for SVG <stroke/> which can't read CSS vars.
   ──────────────────────────────────────────────────────────────────── */
const PRIMARY = "#007C9D";       // hsl(193 100% 31%) — Apollo cyan-teal
const PRIMARY_MID = "#0098B5";   // hsl(192 100% 36%)
const PRIMARY_SOFT = "#46BEC9";  // hsl(186 51% 53%)
const AMBER = "#F59E0B";
const AMBER_DARK = "#D97706";
const ROSE = "#E11D48";
const NEUTRAL = "#94A3B8";


const COMPLIANCE_CHIPS = [
    { key: "needs-attention", label: "Needs attention", dot: ROSE,        tone: "bg-rose-50 text-rose-700 border-rose-200" },
    { key: "at-risk",         label: "At risk",         dot: AMBER,       tone: "bg-amber-50 text-amber-700 border-amber-200" },
    { key: "on-track",        label: "On track",        dot: PRIMARY,     tone: "bg-primary/10 text-primary border-primary/30" },
    { key: "pending",         label: "Pending",         dot: NEUTRAL,     tone: "bg-secondary text-muted-foreground border-border" },
];


export default function CasesPipelineCompliance() {
    const [insights, setInsights] = useState(null);
    const [compliance, setCompliance] = useState(null);
    const [wearTrend, setWearTrend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [activeChip, setActiveChip] = useState(null);
    const [drawer, setDrawer] = useState(null);
    // iter98.99dh — Compliance & Monitoring 2-state filter. "all" |
    // "invisique" | "self". Doctor toggles to slice the dashboard
    // dataset by who runs patient-side compliance for each case.
    const [monitoringFilter, setMonitoringFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            api.get("/doctor/insights/aligner"),
            api.get("/doctor/insights/compliance"),
            api.get("/doctor/insights/wear-trend"),
        ]).then((rs) => {
            if (cancelled) return;
            if (rs[0].status === "fulfilled") setInsights(rs[0].value.data);
            if (rs[1].status === "fulfilled") setCompliance(rs[1].value.data);
            if (rs[2].status === "fulfilled") setWearTrend(rs[2].value.data);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    const items = compliance?.items || [];
    /* iter98.99dh — Monitoring-mode-aware view of the dataset. Applied
       BEFORE the per-bucket counts so KPI strip + donuts reflect the
       active slice. */
    const monitoredItems = useMemo(() => {
        if (monitoringFilter === "invisique") return items.filter((x) => x.coe_support_compliance === true);
        if (monitoringFilter === "self")      return items.filter((x) => x.coe_support_compliance === false);
        return items;
    }, [items, monitoringFilter]);
    const monitoringCounts = useMemo(() => {
        const inv = items.filter((x) => x.coe_support_compliance === true).length;
        const self = items.filter((x) => x.coe_support_compliance === false).length;
        return { invisique: inv, self, all: items.length };
    }, [items]);
    /* When the user has narrowed to a single monitoring mode, derive the
       bucket counts from that subset so the headline KPIs reflect the
       view. Falls back to the API-returned buckets when no monitoring
       filter is applied. */
    const buckets = useMemo(() => {
        if (monitoringFilter === "all") {
            return compliance?.buckets || { "needs-attention": 0, "at-risk": 0, "on-track": 0, pending: 0 };
        }
        const base = { "needs-attention": 0, "at-risk": 0, "on-track": 0, pending: 0 };
        monitoredItems.forEach((it) => { base[it.bucket] = (base[it.bucket] || 0) + 1; });
        return base;
    }, [compliance, monitoredItems, monitoringFilter]);
    const totalActive = buckets["on-track"] + buckets["at-risk"] + buckets["needs-attention"];
    const onTimePct = totalActive > 0 ? Math.round((buckets["on-track"] / totalActive) * 100) : 0;
    const atRisk = buckets["at-risk"] + buckets["needs-attention"];

    const filteredItems = useMemo(() => {
        let list = monitoredItems;
        if (activeChip) list = list.filter((c) => c.bucket === activeChip);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            list = list.filter((c) =>
                (c.patient_name || "").toLowerCase().includes(q)
                || (c.patient_phone || "").toLowerCase().includes(q)
                || (c.case_code || "").toLowerCase().includes(q));
        }
        return list;
    }, [monitoredItems, query, activeChip]);

    /* Reload compliance after threshold change so the buckets update inline. */
    const refreshAfterThresholds = () => {
        api.get("/doctor/insights/compliance")
            .then((r) => setCompliance(r.data))
            .catch(() => { /* non-critical */ });
    };

    return (
        <div className="space-y-4" data-testid="cases-compliance-view">

            {/* iter98.99dh — Compliance & Monitoring 2-state filter
                toggle. Surfaces only when at least one case in either
                mode exists so brand-new doctors with zero submissions
                don't see empty chrome. The toggle drives BOTH the KPI
                strip + donuts + table (via `monitoredItems`). */}
            {(monitoringCounts.invisique > 0 || monitoringCounts.self > 0) && (
                <MonitoringFilterToggle
                    value={monitoringFilter}
                    onChange={setMonitoringFilter}
                    counts={monitoringCounts}
                />
            )}

            {/* ── Compliance KPI strip ── */}
            <ComplianceKpiStrip
                buckets={buckets}
                onTimePct={onTimePct}
                atRisk={atRisk}
                avgWear={insights?.wear || {}}
                openIssues={insights?.issues?.total_open || 0}
                checkIns={insights?.check_ins || {}}
                loading={loading}
                onChipClick={(key) => setActiveChip(activeChip === key ? null : key)}
            />

            {/* ── 4 donut cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <DonutCard
                    title="Aligner pace"
                    icon={<ChartLine size={14} weight="duotone" className="text-primary" />}
                    centerValue={`${onTimePct}%`}
                    centerLabel="On time"
                    legend={[
                        { color: PRIMARY,     label: "On time",            value: buckets["on-track"] },
                        { color: AMBER,       label: "Delay (mild)",       value: buckets["at-risk"] },
                        { color: ROSE,        label: "Delay (significant)", value: buckets["needs-attention"] },
                    ]}
                    testid="donut-aligner-pace"
                />
                <DonutCard
                    title="Wear discipline"
                    icon={<ChartLine size={14} weight="duotone" className="text-primary" />}
                    centerValue={(() => {
                        const w = insights?.wear || {};
                        const ok = w.on_track || 0;
                        const totalScored = ok + (w.at_risk || 0) + (w.needs_attention || 0);
                        return totalScored > 0 ? `${Math.round(ok / totalScored * 100)}%` : "—";
                    })()}
                    centerLabel="On track"
                    legend={[
                        { color: PRIMARY,     label: "On track (≥20h)",        value: insights?.wear?.on_track || 0 },
                        { color: AMBER,       label: "At risk (16–19h)",       value: insights?.wear?.at_risk || 0 },
                        { color: ROSE,        label: "Critical (<16h)",        value: insights?.wear?.needs_attention || 0 },
                        { color: NEUTRAL,     label: "No data yet",            value: insights?.wear?.no_data || 0 },
                    ]}
                    testid="donut-wear-discipline"
                />
                <DonutCard
                    title="Check-in streak"
                    icon={<ChartLine size={14} weight="duotone" className="text-primary" />}
                    centerValue={(() => {
                        const c = insights?.check_ins || {};
                        const ok = c.up_to_date || 0;
                        const total = ok + (c.missing_this_week || 0);
                        return total > 0 ? `${Math.round(ok / total * 100)}%` : "—";
                    })()}
                    centerLabel="This week"
                    legend={[
                        { color: PRIMARY,     label: "Up to date",         value: insights?.check_ins?.up_to_date || 0 },
                        { color: AMBER,       label: "Missing this week",  value: insights?.check_ins?.missing_this_week || 0 },
                    ]}
                    testid="donut-checkins"
                />
                <DonutCard
                    title="Reported concerns"
                    icon={<ChartLine size={14} weight="duotone" className="text-primary" />}
                    centerValue={String(insights?.issues?.total_open ?? 0)}
                    centerLabel="Open issues"
                    legend={[
                        { color: ROSE,        label: "Missing aligner",    value: insights?.issues?.by_type?.missing_aligner || 0 },
                        { color: AMBER,       label: "Broken aligner",     value: insights?.issues?.by_type?.broken_aligner || 0 },
                        { color: PRIMARY,     label: "Gum irritation",     value: insights?.issues?.by_type?.gum_irritation || 0 },
                        { color: NEUTRAL,     label: "Other",              value: (insights?.issues?.by_type?.sharp_edges || 0) + (insights?.issues?.by_type?.other || 0) },
                    ]}
                    testid="donut-issues"
                />
            </div>

            {/* ── Wear trend line chart ── */}
            <WearTrendChart wearTrend={wearTrend} loading={loading} />

            {/* ── Compliance table ── */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="compliance-table">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-2 text-sm font-medium">
                        <UserCircle size={15} weight="duotone" className="text-primary" /> Compliance roster
                        <span className="text-[10px] text-muted-foreground bg-secondary rounded-full px-2 py-0.5">
                            {filteredItems.length} of {items.length}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-2 bg-secondary/60 border border-border rounded-lg px-2.5 py-1.5">
                            <MagnifyingGlass size={12} className="text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search patient…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-[12px] placeholder:text-muted-foreground w-40"
                                data-testid="compliance-search-input"
                            />
                        </div>
                        {COMPLIANCE_CHIPS.map((chip) => {
                            const active = activeChip === chip.key;
                            const count = buckets[chip.key] || 0;
                            return (
                                <button
                                    key={chip.key}
                                    onClick={() => setActiveChip(active ? null : chip.key)}
                                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                                        active ? chip.tone : "bg-background border-border text-muted-foreground hover:bg-secondary"
                                    }`}
                                    data-testid={`compliance-chip-${chip.key}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: chip.dot }} />
                                    {chip.label}
                                    <span className="text-[9px] font-bold bg-black/5 px-1.5 py-0.5 rounded-full">{count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-secondary/60">
                            <tr>
                                <Th>Patient</Th>
                                <Th>Wear (30d trend)</Th>
                                <Th>Stage</Th>
                                <Th>Last touch</Th>
                                <Th>Bucket</Th>
                                <Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">Loading compliance data…</td></tr>
                            )}
                            {!loading && filteredItems.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                                    {items.length === 0
                                        ? "No active patients yet."
                                        : <button onClick={() => { setActiveChip(null); setQuery(""); }} className="text-primary font-semibold underline">Clear filters</button>}
                                </td></tr>
                            )}
                            {!loading && filteredItems.map((item) => (
                                <ComplianceRow
                                    key={item.case_id}
                                    item={item}
                                    onOpenDrawer={() => setDrawer({ patient_id: item.patient_id, case_id: item.case_id, bucket: item.bucket })}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Threshold editor ── */}
            <div id="alert-thresholds-anchor" className="scroll-mt-24">
                <ThresholdEditor onSaved={refreshAfterThresholds} />
            </div>

            {/* ── Daily drift digest preferences ── */}
            <DigestPreferencesCard />

            {/* ── Drawer ── */}
            <PatientInsightsDrawer
                open={!!drawer}
                patientId={drawer?.patient_id}
                caseId={drawer?.case_id}
                bucket={drawer?.bucket}
                onClose={() => setDrawer(null)}
            />
        </div>
    );
}


/* ─────────────────────────── SUBCOMPONENTS ─────────────────────────── */

function Th({ children, className = "" }) {
    return (
        <th className={`text-left px-4 py-2.5 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border ${className}`}>{children}</th>
    );
}


function ComplianceKpiStrip({ buckets, onTimePct, atRisk, avgWear, openIssues, checkIns, loading, onChipClick }) {
    const cards = [
        { val: `${onTimePct}%`, label: "On time", tone: "primary", hint: "Across all active aligners", key: "on-track" },
        { val: buckets["at-risk"] || 0, label: "At risk", tone: "amber", hint: "Mild deviation", key: "at-risk" },
        { val: buckets["needs-attention"] || 0, label: "Needs attention", tone: "rose", hint: "Critical", key: "needs-attention" },
        { val: openIssues, label: "Open issues", tone: openIssues > 0 ? "rose" : "muted", hint: "Last 28 days" },
        { val: `${(avgWear.on_track || 0)} / ${(avgWear.on_track || 0) + (avgWear.at_risk || 0) + (avgWear.needs_attention || 0) || 0}`, label: "Wear compliant", tone: "primary", hint: "≥20h/day" },
        { val: `${checkIns.up_to_date || 0} / ${(checkIns.up_to_date || 0) + (checkIns.missing_this_week || 0)}`, label: "Check-ins this week", tone: "primary", hint: "Photo submitted" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="compliance-kpi-strip">
            {cards.map((c, i) => (
                <button
                    key={i}
                    onClick={() => c.key && onChipClick?.(c.key)}
                    className={`text-left bg-card border border-border rounded-xl px-4 py-3 transition-all ${
                        c.key ? "cursor-pointer hover:border-primary/40 hover:shadow-sm" : "cursor-default"
                    }`}
                    data-testid={`compliance-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <div className={`text-xl font-bold tabular-nums leading-none ${
                        c.tone === "primary" ? "text-primary"
                        : c.tone === "amber" ? "text-amber-700"
                        : c.tone === "rose" ? "text-rose-600"
                        : c.tone === "muted" ? "text-muted-foreground"
                        : "text-foreground"
                    }`}>
                        {loading ? "—" : c.val}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{c.label}</div>
                    <div className="text-[10px] text-muted-foreground/80 mt-0.5">{c.hint}</div>
                </button>
            ))}
        </div>
    );
}


function DonutCard({ title, icon, centerValue, centerLabel, legend = [], testid }) {
    return (
        <div className="relative bg-card border border-border rounded-xl p-4 flex flex-col" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                {icon}
                <h3 className="text-[12px] font-semibold leading-snug flex-1 truncate">{title}</h3>
            </div>
            <div className="flex items-center gap-4">
                <Donut segments={legend} size={92}>
                    <div className="font-display text-lg font-bold tabular-nums text-foreground leading-none">{centerValue}</div>
                    {centerLabel && <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">{centerLabel}</div>}
                </Donut>
                <div className="flex-1 min-w-0 space-y-1">
                    {legend.map((s) => (
                        <div key={s.label} className="flex items-center gap-1.5 text-[11px]">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
                            <span className="tabular-nums font-semibold w-5 shrink-0">{s.value || 0}</span>
                            <span className="text-muted-foreground truncate">{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}


function Donut({ segments, size = 100, children }) {
    const stroke = 10;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const total = segments.reduce((s, sg) => s + (sg.value || 0), 0);
    const safeTotal = total || 1;
    let offset = 0;
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90 block">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
                {total > 0 && segments.map((seg, i) => {
                    const len = ((seg.value || 0) / safeTotal) * c;
                    if (len <= 0) return null;
                    const el = (
                        <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
                            stroke={seg.color} strokeWidth={stroke}
                            strokeDasharray={`${len} ${c}`} strokeDashoffset={-offset} strokeLinecap="butt" />
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-1">
                {children}
            </div>
        </div>
    );
}


/* My-patients wear-trend — 30-day daily avg across this doctor's
 * in-treatment cases. Single-line SVG chart with a dotted target line. */
function WearTrendChart({ wearTrend, loading }) {
    const series = wearTrend?.series || [];
    const target = wearTrend?.target || 22;
    const avg = wearTrend?.avg;
    const W = 720;
    const H = 130;
    const pad = { top: 14, right: 14, bottom: 22, left: 30 };
    const w = W - pad.left - pad.right;
    const h = H - pad.top - pad.bottom;
    const yMax = 24;
    const pointsArr = series.map((p, i) => {
        const x = pad.left + (i / Math.max(1, series.length - 1)) * w;
        const y = pad.top + h - (Math.min(p.avg_hours, yMax) / yMax) * h;
        return [x, y, p];
    });
    const path = pointsArr.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const areaPath = pointsArr.length > 0
        ? `${path} L ${pad.left + w},${pad.top + h} L ${pad.left},${pad.top + h} Z`
        : "";
    const targetY = pad.top + h - (target / yMax) * h;

    if (loading) {
        return (
            <div className="bg-card border border-border rounded-xl p-10 text-center text-sm text-muted-foreground">
                Loading wear trend…
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-xl p-4" data-testid="wear-trend-chart">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="inline-flex items-center gap-2">
                    <ChartLine size={15} weight="duotone" className="text-primary" />
                    <h3 className="text-sm font-semibold">My patients&apos; wear trend</h3>
                    <span className="text-[10px] text-muted-foreground">last 30 days</span>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                    <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-px bg-primary" /> Daily avg
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-px border-t border-dashed border-amber-500" /> Target {target}h
                    </span>
                    {avg != null && (
                        <span className="font-semibold tabular-nums text-foreground">
                            avg <span className="text-primary">{avg}h</span>
                        </span>
                    )}
                </div>
            </div>
            {series.length === 0 ? (
                <div className="py-10 text-center text-xs text-muted-foreground">No wear data yet.</div>
            ) : (
                <div className="relative">
                    {/* HTML Y-axis labels overlay — sharper than scaled <svg text>. */}
                    <div className="absolute inset-0 pointer-events-none">
                        {[0, 8, 16, 22, 24].map((v) => {
                            const topPct = (pad.top + h - (v / yMax) * h) / H * 100;
                            return (
                                <span
                                    key={v}
                                    className="absolute text-[10px] text-muted-foreground tabular-nums"
                                    style={{ top: `${topPct}%`, left: 2, transform: "translateY(-50%)" }}
                                >
                                    {v}h
                                </span>
                            );
                        })}
                    </div>
                    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="block" preserveAspectRatio="none" style={{ height: H }}>
                        {/* gridlines */}
                        {[0, 8, 16, 22, 24].map((v) => {
                            const y = pad.top + h - (v / yMax) * h;
                            return (
                                <line key={v} x1={pad.left} x2={pad.left + w} y1={y} y2={y} stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray={v === target ? "3 3" : "0"} opacity={v === target ? 0.8 : 0.4} />
                            );
                        })}
                        {/* area */}
                        <path d={areaPath} fill={PRIMARY} fillOpacity="0.10" />
                        {/* target line emphasis */}
                        <line x1={pad.left} x2={pad.left + w} y1={targetY} y2={targetY} stroke={AMBER} strokeWidth="1" strokeDasharray="4 4" />
                        {/* main line */}
                        <path d={path} fill="none" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        {/* points */}
                        {pointsArr.map(([x, y, p], i) => (
                            <circle key={i} cx={x} cy={y} r={p.logged_cases > 0 ? 2.5 : 1} fill={p.logged_cases > 0 ? PRIMARY : NEUTRAL} vectorEffect="non-scaling-stroke">
                                <title>{`${p.date}: ${p.avg_hours}h avg (${p.logged_cases} cases logged)`}</title>
                            </circle>
                        ))}
                    </svg>
                    {/* HTML X-axis date ticks overlay */}
                    <div className="relative h-4 mt-1">
                        {[0, Math.floor(series.length / 2), series.length - 1].filter((i, idx, a) => a.indexOf(i) === idx).map((i) => {
                            const [x] = pointsArr[i] || [];
                            if (x == null) return null;
                            const leftPct = (x / W) * 100;
                            const d = new Date(series[i].date + "T00:00:00Z");
                            return (
                                <span
                                    key={i}
                                    className="absolute text-[10px] text-muted-foreground tabular-nums"
                                    style={{ left: `${leftPct}%`, transform: "translateX(-50%)" }}
                                >
                                    {d.toLocaleDateString([], { day: "numeric", month: "short" })}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}


function ComplianceRow({ item, onOpenDrawer }) {
    const chip = COMPLIANCE_CHIPS.find((c) => c.key === item.bucket);
    const wear = item.wear_7d_avg;
    const trend = item.wear_trend;
    const series = item.wear_30d_series || [];
    const sparkColor = wear == null ? NEUTRAL
        : wear < 12 ? ROSE
        : wear < 16 ? AMBER
        : PRIMARY;

    const firstName = (item.patient_name || "").split(" ")[0] || "there";
    const nudgeBody = `Hi ${firstName}, quick check-in from your aligner team. How are the aligners feeling today? Reply with a 👍 if everything's on track.`;
    const nudgeHref = waLink(item.patient_phone, nudgeBody);
    const nudgeBlockReason = waReason(item.patient_phone);

    return (
        <tr
            onClick={onOpenDrawer}
            className="group cursor-pointer border-b border-border/60 last:border-b-0 hover:bg-secondary/60 transition-colors"
            data-testid={`compliance-row-${item.case_id}`}
        >
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                    {item.patient_photo_data_url ? (
                        <img
                            src={item.patient_photo_data_url}
                            alt={item.patient_name || "Patient"}
                            className="w-9 h-9 rounded-full object-cover border border-primary/20 shrink-0 group-hover:border-primary/40 transition"
                        />
                    ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold text-sm shrink-0 group-hover:bg-primary/15 transition">
                            {(item.patient_name || "P").slice(0, 1).toUpperCase()}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="font-medium text-foreground truncate group-hover:text-primary transition flex items-center gap-1.5">
                            <span className="truncate">{item.patient_name || "—"}</span>
                            {/* iter98.99dh — Monitoring mode marker
                                mirrors the pipeline-table icon vocab. */}
                            {item.coe_support_compliance === true && (
                                <span
                                    className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary/10 text-primary ring-1 ring-primary/25 shrink-0"
                                    title="Invisique™ compliance & monitoring · active"
                                    data-testid={`compliance-row-monitoring-${item.case_id}-invisique`}
                                >
                                    <ShieldCheck size={11} weight="fill" />
                                </span>
                            )}
                            {item.coe_support_compliance === false && (
                                <span
                                    className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-300 shrink-0"
                                    title="Self-monitored compliance · doctor managed"
                                    data-testid={`compliance-row-monitoring-${item.case_id}-self`}
                                >
                                    <Stethoscope size={11} weight="duotone" />
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate font-mono">{item.case_code || "—"}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                    <div className="shrink-0 text-right">
                        {wear != null ? (
                            <>
                                <div className="text-[12px] font-semibold tabular-nums">{wear}h</div>
                                <div className="text-[10px] text-muted-foreground tabular-nums">7d avg</div>
                            </>
                        ) : (
                            <>
                                <div className="text-[12px] text-muted-foreground/70">—</div>
                                <div className="text-[10px] text-muted-foreground">no logs</div>
                            </>
                        )}
                    </div>
                    <div style={{ color: sparkColor }}>
                        <Sparkline data={series} width={80} height={26} targetLine={22} />
                    </div>
                    {trend === "up" && <TrendUp size={12} weight="bold" className="text-primary shrink-0" />}
                    {trend === "down" && <TrendDown size={12} weight="bold" className="text-rose-600 shrink-0" />}
                </div>
            </td>
            <td className="px-4 py-2.5 text-[12px] tabular-nums">
                <span className="font-semibold">{item.stage || 0}</span>
                <span className="text-muted-foreground"> / {item.total_stages || "—"}</span>
                {item.stage_day != null && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">day {item.stage_day} of {item.stage_total_days}</div>
                )}
            </td>
            <td className="px-4 py-2.5 text-[12px] text-muted-foreground">
                <LastTouch ts={item.last_touch_at} type={item.last_touch_type} />
            </td>
            <td className="px-4 py-2.5">
                {chip && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${chip.tone}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: chip.dot }} />
                        {chip.label}
                    </span>
                )}
            </td>
            <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1.5">
                    {nudgeHref ? (
                        <a
                            href={nudgeHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                api.post("/doctor/nudges", {
                                    patient_id: item.patient_id,
                                    case_id: item.case_id,
                                    reason: item.bucket || "manual_nudge",
                                    message: nudgeBody,
                                    channel: "whatsapp",
                                }).catch(() => { /* non-critical */ });
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-primary/10 text-primary hover:bg-primary/15 border border-primary/30"
                            title="Send a WhatsApp nudge"
                            data-testid={`compliance-row-nudge-${item.case_id}`}
                        >
                            <ChatCircleDots size={11} weight="duotone" /> Nudge
                        </a>
                    ) : (
                        <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-secondary text-muted-foreground border border-border cursor-not-allowed"
                            title={nudgeBlockReason || "WhatsApp unavailable"}
                        >
                            <ChatCircleDots size={11} weight="duotone" /> Nudge
                        </span>
                    )}
                    <Link
                        to={`/doctor/patients/${item.patient_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-card hover:bg-secondary border border-border text-foreground"
                        title="Open patient profile"
                        data-testid={`compliance-row-open-${item.case_id}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <UserCircle size={11} weight="duotone" /> Open
                    </Link>
                </div>
            </td>
        </tr>
    );
}


function LastTouch({ ts, type }) {
    if (!ts) return <span className="text-[11px] text-muted-foreground/60">—</span>;
    const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    let label;
    if (Number.isNaN(days)) label = "—";
    else if (days <= 0) label = "Today";
    else if (days === 1) label = "Yesterday";
    else if (days < 7) label = `${days}d ago`;
    else if (days < 30) label = `${Math.floor(days / 7)}w ago`;
    else label = `${Math.floor(days / 30)}mo ago`;
    const typeLabel = type === "appointment" ? "Appt" : type === "patient_message" ? "Chat" : type === "nudge" ? "Nudge" : "—";
    return (
        <div className="text-[12px]">
            <div className="font-medium">{label}</div>
            <div className="text-[10px] text-muted-foreground">{typeLabel}</div>
        </div>
    );
}


/* ─────────────────────────── THRESHOLD EDITOR ─────────────────────── */

function ThresholdEditor({ onSaved }) {
    const [data, setData] = useState(null); // { thresholds, defaults, is_custom, updated_at }
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/doctor/compliance-thresholds")
            .then((r) => {
                setData(r.data);
                setForm({ ...r.data.thresholds });
            })
            .catch(() => setError("Couldn't load thresholds"));
    }, []);

    const dirty = useMemo(() => {
        if (!form || !data) return false;
        return Object.keys(form).some((k) => Number(form[k]) !== Number(data.thresholds[k]));
    }, [form, data]);

    const validate = (f) => {
        if (Number(f.wear_at_risk_min) >= Number(f.wear_on_track_min)) {
            return "At-risk floor must be lower than on-track floor.";
        }
        if (Number(f.log_stale_warn_days) >= Number(f.log_stale_critical_days)) {
            return "Warn days must be lower than critical days.";
        }
        return null;
    };

    const save = async () => {
        const err = validate(form);
        if (err) { setError(err); return; }
        setError(null);
        setSaving(true);
        try {
            const r = await api.put("/doctor/compliance-thresholds", form);
            setData((d) => ({ ...d, thresholds: r.data.thresholds, is_custom: true, updated_at: r.data.updated_at }));
            toast.success("Alert thresholds saved", { description: "Compliance buckets will recompute on the next refresh." });
            onSaved?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't save thresholds");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!data?.is_custom) return;
        setSaving(true);
        try {
            await api.delete("/doctor/compliance-thresholds");
            const r = await api.get("/doctor/compliance-thresholds");
            setData(r.data);
            setForm({ ...r.data.thresholds });
            toast.success("Reset to defaults");
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    if (!data || !form) {
        return (
            <div className="bg-card border border-border rounded-xl p-4 text-sm text-muted-foreground">
                <Sliders size={14} className="inline mr-1.5" /> Loading thresholds…
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="threshold-editor">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2 bg-gradient-to-r from-primary/5 to-primary/0">
                <div className="inline-flex items-center gap-2">
                    <Sliders size={15} className="text-primary" weight="duotone" />
                    <h3 className="text-sm font-semibold">Alert thresholds</h3>
                    {data.is_custom ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/30">
                            <CheckCircle size={10} weight="fill" /> custom
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-full border border-border">
                            defaults
                        </span>
                    )}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Info size={11} /> Drives the buckets shown above.
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <ThresholdInput
                    label="On-track floor"
                    sub="Avg wear ≥ this is on-track (hours/day)"
                    value={form.wear_on_track_min}
                    onChange={(v) => setForm({ ...form, wear_on_track_min: v })}
                    min={10} max={24} step={0.5} unit="h"
                    defaultValue={data.defaults.wear_on_track_min}
                    testid="threshold-wear-on-track"
                />
                <ThresholdInput
                    label="At-risk floor"
                    sub="Below this avg is critical (hours/day)"
                    value={form.wear_at_risk_min}
                    onChange={(v) => setForm({ ...form, wear_at_risk_min: v })}
                    min={4} max={22} step={0.5} unit="h"
                    defaultValue={data.defaults.wear_at_risk_min}
                    testid="threshold-wear-at-risk"
                />
                <ThresholdInput
                    label="Log-staleness warn"
                    sub="Days quiet → 'at risk'"
                    value={form.log_stale_warn_days}
                    onChange={(v) => setForm({ ...form, log_stale_warn_days: Math.round(v) })}
                    min={1} max={14} step={1} unit="d"
                    defaultValue={data.defaults.log_stale_warn_days}
                    testid="threshold-log-warn"
                />
                <ThresholdInput
                    label="Log-staleness critical"
                    sub="Days quiet → 'needs attention'"
                    value={form.log_stale_critical_days}
                    onChange={(v) => setForm({ ...form, log_stale_critical_days: Math.round(v) })}
                    min={2} max={30} step={1} unit="d"
                    defaultValue={data.defaults.log_stale_critical_days}
                    testid="threshold-log-critical"
                />
            </div>

            {error && (
                <div className="mx-4 mb-4 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-[12px] inline-flex items-start gap-1.5">
                    <Warning size={12} className="mt-0.5 shrink-0" weight="fill" /> {error}
                </div>
            )}

            <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-2 bg-secondary/30">
                <div className="text-[11px] text-muted-foreground">
                    {data.updated_at ? `Updated ${new Date(data.updated_at).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}` : "Defaults active"}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={reset}
                        disabled={saving || !data.is_custom}
                        className="text-xs rounded-full"
                        data-testid="threshold-reset-btn"
                    >
                        <ArrowsClockwise size={13} className="mr-1.5" /> Reset
                    </Button>
                    <Button
                        size="sm"
                        onClick={save}
                        disabled={saving || !dirty}
                        className="text-xs rounded-full"
                        data-testid="threshold-save-btn"
                    >
                        <FloppyDisk size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save thresholds"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function ThresholdInput({ label, sub, value, onChange, min, max, step, unit, defaultValue, testid }) {
    const isOverride = Number(value) !== Number(defaultValue);
    return (
        <div className="bg-background border border-border rounded-lg p-3" data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <div className="text-[12px] font-semibold">{label}</div>
                {isOverride && <span className="text-[9px] font-bold uppercase text-primary">override</span>}
            </div>
            <div className="text-[10px] text-muted-foreground mb-2">{sub}</div>
            <div className="flex items-center gap-3">
                <input
                    type="range"
                    min={min} max={max} step={step}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="flex-1 accent-primary cursor-pointer"
                    data-testid={`${testid}-slider`}
                />
                <div className="flex items-center gap-1 text-[12px] font-bold tabular-nums w-16 justify-end">
                    <span>{Number(value).toFixed(step < 1 ? 1 : 0)}</span>
                    <span className="text-muted-foreground">{unit}</span>
                </div>
            </div>
            <div className="text-[9px] text-muted-foreground mt-1 tabular-nums">
                Default {defaultValue}{unit} · range {min}–{max}{unit}
            </div>
        </div>
    );
}


/* ─────────────────────── DIGEST PREFERENCES CARD ─────────────────────
 * Sits under the threshold editor. Two-row layout:
 *   Row 1: opt-in toggle · severity radios · email override · resolved-email hint
 *   Row 2: last-run state · "Send now" affordance
 * The "Send now" button hits POST /run which overrides today's idempotency
 * so a doctor can re-fire the email for testing.
 */
const SEVERITY_OPTIONS = [
    { value: "needs_attention", label: "Only critical", hint: "Email when a patient enters Needs attention" },
    { value: "any", label: "All worsening", hint: "Email on any bucket downgrade" },
    { value: "none", label: "Off", hint: "Disable drift emails" },
];

function DigestPreferencesCard() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/doctor/compliance-digest/preferences")
            .then((r) => {
                setData(r.data);
                setForm({ ...r.data.preferences, email_override: r.data.preferences.email_override || "" });
            })
            .catch(() => setError("Couldn't load preferences"));
    }, []);

    const dirty = useMemo(() => {
        if (!form || !data) return false;
        const orig = data.preferences;
        return (
            (form.enabled !== orig.enabled) ||
            (form.severity !== orig.severity) ||
            ((form.email_override || null) !== (orig.email_override || null))
        );
    }, [form, data]);

    const save = async () => {
        setError(null);
        setSaving(true);
        try {
            const r = await api.put("/doctor/compliance-digest/preferences", {
                enabled: form.enabled,
                severity: form.severity,
                email_override: form.email_override?.trim() || null,
            });
            setData(r.data);
            setForm({ ...r.data.preferences, email_override: r.data.preferences.email_override || "" });
            toast.success("Digest preferences saved");
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't save preferences");
        } finally {
            setSaving(false);
        }
    };

    const sendNow = async () => {
        setSending(true);
        try {
            const r = await api.post("/doctor/compliance-digest/run");
            if (r.data.email_sent) {
                toast.success(`Drift digest sent (${r.data.drift_count} alert${r.data.drift_count !== 1 ? "s" : ""})`);
            } else if (r.data.drift_count === 0) {
                toast.info("No drift overnight — nothing to send");
            } else {
                toast.warning("Digest computed but email was skipped", {
                    description: r.data.skipped_reason || "Check email override + doctor email on file",
                });
            }
            // Re-read the prefs so last_run_date refreshes.
            const refreshed = await api.get("/doctor/compliance-digest/preferences");
            setData(refreshed.data);
        } finally {
            setSending(false);
        }
    };

    if (!data || !form) {
        return (
            <div className="bg-card border border-border rounded-xl p-4 text-sm text-muted-foreground">
                <Bell size={14} className="inline mr-1.5" /> Loading digest preferences…
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="digest-preferences-card">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap bg-gradient-to-r from-primary/5 to-primary/0">
                <div className="inline-flex items-center gap-2">
                    <Bell size={15} weight="duotone" className="text-primary" />
                    <h3 className="text-sm font-semibold">Daily drift digest</h3>
                    {data.last_drift_count != null && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-full border border-border">
                            last run: {data.last_drift_count} alert{data.last_drift_count !== 1 ? "s" : ""}
                        </span>
                    )}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Info size={11} /> Fires once per day on first dashboard load.
                </div>
            </div>

            <div className="p-4 space-y-3">
                {/* Row 1 — enabled toggle */}
                <div className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg p-3">
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold">Email me when patients drift</div>
                        <div className="text-[11px] text-muted-foreground">
                            Single daily email to <span className="font-medium text-foreground">{data.resolved_email || "—"}</span> with every patient who slipped into a worse bucket overnight.
                        </div>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={form.enabled}
                        onClick={() => setForm({ ...form, enabled: !form.enabled })}
                        className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${form.enabled ? "bg-primary" : "bg-border"}`}
                        data-testid="digest-enabled-toggle"
                    >
                        <span
                            className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                            style={{ transform: form.enabled ? "translateX(16px)" : "translateX(0)" }}
                        />
                    </button>
                </div>

                {/* Row 2 — severity radios */}
                <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-[12px] font-semibold mb-2">Trigger threshold</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {SEVERITY_OPTIONS.map((opt) => {
                            const active = form.severity === opt.value;
                            const disabled = !form.enabled && opt.value !== "none";
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setForm({ ...form, severity: opt.value })}
                                    disabled={disabled}
                                    className={`text-left px-3 py-2 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                        active
                                            ? "border-primary/40 bg-primary/5 ring-1 ring-primary/30"
                                            : "border-border bg-card hover:border-primary/20"
                                    }`}
                                    data-testid={`digest-severity-${opt.value}`}
                                >
                                    <div className="text-[12px] font-semibold flex items-center gap-1.5">
                                        <span className={`w-3 h-3 rounded-full border ${active ? "bg-primary border-primary" : "border-border"}`} />
                                        {opt.label}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.hint}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Row 3 — email override */}
                <div className="bg-background border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-[12px] font-semibold">Email override</div>
                        <div className="text-[10px] text-muted-foreground">Leave blank to use your account email</div>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-secondary/60 border border-border rounded-lg px-2.5 py-1.5 w-full">
                        <EnvelopeSimple size={12} className="text-muted-foreground" />
                        <input
                            type="email"
                            placeholder="hello@clinic.com"
                            value={form.email_override}
                            onChange={(e) => setForm({ ...form, email_override: e.target.value })}
                            className="flex-1 bg-transparent outline-none text-[12px] placeholder:text-muted-foreground"
                            data-testid="digest-email-override-input"
                        />
                    </div>
                </div>

                {error && (
                    <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-[12px] inline-flex items-start gap-1.5 w-full">
                        <Warning size={12} className="mt-0.5 shrink-0" weight="fill" /> {error}
                    </div>
                )}
            </div>

            <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-2 bg-secondary/30 flex-wrap">
                <div className="text-[11px] text-muted-foreground">
                    {data.last_run_date ? (
                        <>Last sent <span className="font-semibold text-foreground">{new Date(data.last_run_date + "T00:00:00Z").toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}</span></>
                    ) : (
                        "Not run yet"
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={sendNow}
                        disabled={sending}
                        className="text-xs rounded-full"
                        data-testid="digest-send-now-btn"
                    >
                        <PaperPlaneTilt size={13} className="mr-1.5" /> {sending ? "Sending…" : "Send now"}
                    </Button>
                    <Button
                        size="sm"
                        onClick={save}
                        disabled={saving || !dirty}
                        className="text-xs rounded-full"
                        data-testid="digest-save-btn"
                    >
                        <FloppyDisk size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save preferences"}
                    </Button>
                </div>
            </div>
        </div>
    );
}



/* MonitoringFilterToggle — slim 3-way segmented control at the top of
   the Compliance dashboard. Lets the doctor slice the entire dashboard
   (KPI strip · donuts · roster table) by who runs patient-side
   compliance for each case.

   iter98.99dh — Implements the Compliance-tab half of Option G: the
   icon vocabulary mirrors what the Pipeline table uses next to each
   patient name (ShieldCheck = Invisique-monitored, Stethoscope =
   self-monitored) so the two surfaces feel like one product.

   Hidden entirely when no cases have committed to a monitoring mode
   yet (e.g. brand-new doctor account). */
function MonitoringFilterToggle({ value, onChange, counts }) {
    const segments = [
        { key: "all",       label: "All",                  count: counts.all,        Icon: null,         tone: "neutral" },
        { key: "invisique", label: "Invisique-monitored",  count: counts.invisique,  Icon: ShieldCheck,  tone: "primary" },
        { key: "self",      label: "Self-monitored",       count: counts.self,       Icon: Stethoscope,  tone: "slate" },
    ];
    return (
        <div
            className="bg-card border border-border rounded-xl p-1 inline-flex items-center gap-1 self-start"
            role="radiogroup"
            aria-label="Filter by compliance & monitoring mode"
            data-testid="compliance-monitoring-toggle"
        >
            {segments.map((s) => {
                const active = value === s.key;
                const Icon = s.Icon;
                const activeCls = active
                    ? (s.tone === "primary" ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                       : s.tone === "slate" ? "bg-slate-100 text-slate-700 ring-1 ring-slate-300"
                                            : "bg-secondary text-foreground ring-1 ring-border")
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground";
                return (
                    <button
                        key={s.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onChange(s.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${activeCls}`}
                        data-testid={`compliance-monitoring-toggle-${s.key}`}
                    >
                        {Icon ? <Icon size={13} weight={active ? "fill" : "duotone"} /> : null}
                        {s.label}
                        <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${active ? "bg-white/60 text-foreground" : "bg-secondary text-muted-foreground"}`}>
                            {s.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
